import { unparse } from 'papaparse';
import { zip } from 'ramda';

import { requestGoogleAccess } from 'core/googleClient';

export type Line = Record<string, string | number | null>;

const rangeSizeToColumnName = (size: number): string => {
  if (size <= 0) {
    return '';
  }
  // basically doing a base 26 encoding apart from:
  // 0 is the empty char and we start counting form 'A'
  const encoding = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const remaining = (size - 1) % encoding.length;
  const divider = Math.floor((size - 1) / encoding.length);
  const lastChar = encoding[remaining];
  return divider === 0 ? lastChar : rangeSizeToColumnName(divider) + lastChar;
};

const buildGoogleSheetsRange = (columnCount: number, rowCount: number, sheetName: string = 'Sheet1'): string =>
  `${sheetName}!A1:${rangeSizeToColumnName(columnCount)}${rowCount}`;

const collectAllColumnNames = (records: Line[]): string[] => {
  const memo = new Set<string>();
  return records
    .flatMap(record => Object.keys(record))
    .reduce((acc: string[], columnName: string) => {
      if (!memo.has(columnName)) {
        memo.add(columnName);
        return [...acc, columnName];
      }
      return acc;
    }, []);
};

const recordToFlatLine = (columns: string[], record: Line) => columns.map(columnName => record[columnName] ?? null);

const recordsToTabularData = (records: Line[]): (string | number | null)[][] => {
  const columns = collectAllColumnNames(records);
  const flatLines = records.map(record => recordToFlatLine(columns, record));
  return [columns as (string | number | null)[]].concat(flatLines);
};

export type ColumnWidth = {
  columnIndex: number;
  width: number;
};

export type ColumnFormat = {
  columnIndex: number;
  format: string;
  type: 'NUMBER' | 'CURRENCY';
};

export type ValidationRule = gapi.client.sheets.DataValidationRule;

export type ColumnValidation = {
  columnIndex: number;
  rule: ValidationRule;
};

type SheetBasicData = {
  name: string;
  cellData: (string | number | null)[][];
  formats?: ColumnFormat[];
  validations?: ColumnValidation[];
  widths?: ColumnWidth[];
};

export const toSheet = (
  name: string,
  records: Line[],
  formats?: ColumnFormat[],
  validations?: ColumnValidation[],
  widths?: ColumnWidth[],
): SheetBasicData => {
  const cellData = recordsToTabularData(records);
  return { name, cellData, formats, validations, widths };
};

export const fetchGoogleSheetsData = async (
  spreadsheetId: string,
  sheetName: string | undefined = undefined,
): Promise<Line[]> => {
  const gapi = await requestGoogleAccess();

  const spreadsheet = await gapi.client.sheets.spreadsheets.get({ spreadsheetId }).then(response => response.result);
  const sheetData = sheetName
    ? spreadsheet.sheets?.find(sheet => sheet.properties?.title === sheetName)
    : spreadsheet.sheets?.[0];

  const sheet = sheetData?.properties;
  const grid = sheet?.gridProperties;

  if (!sheet || !grid?.columnCount || !grid?.rowCount) {
    throw new Error('Sheet not found');
  }

  const ranges = [buildGoogleSheetsRange(grid.columnCount, grid.rowCount, sheet.title)];
  const cellData = await gapi.client.sheets.spreadsheets.values
    .batchGet({
      spreadsheetId,
      ranges,
      valueRenderOption: 'UNFORMATTED_VALUE',
    })
    .then(response => response.result.valueRanges);
  const firstCellData = cellData?.[0] ?? null;

  if (!firstCellData) {
    throw new Error('No data found');
  }

  const [columns, ...values] = firstCellData.values!;
  return values.map(row => Object.fromEntries(zip(columns, Array.from({ ...row, length: columns.length }))));
};

export const makeCsvFile = (lines: Line[], filename: string) => {
  const type = 'text/csv';
  const csvString = unparse(lines);
  const utf8Array = new TextEncoder().encode(csvString);
  return new File([utf8Array], filename, { type });
};

export const selectGoogleSpreadsheet = async (): Promise<google.picker.DocumentObject | null> => {
  const gapi = await requestGoogleAccess();
  return new Promise(resolve => {
    const callback = (data: google.picker.ResponseObject) => {
      if (data.action === google.picker.Action.PICKED && data.docs[0]) {
        resolve(data.docs[0]);
      } else if (data.action === google.picker.Action.CANCEL) {
        resolve(null);
      }
    };
    const picker = new google.picker.PickerBuilder()
      .addView(google.picker.ViewId.SPREADSHEETS)
      .disableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(gapi.client.getToken().access_token)
      .setCallback(callback)
      .build();
    picker.setVisible(true);
  });
};

const addSheetRequest = (sheet: SheetBasicData, sheetId: number) => ({
  addSheet: {
    properties: {
      title: sheet.name,
      sheetId,
      index: 0,
      gridProperties: {
        rowCount: sheet.cellData.length,
        columnCount: sheet.cellData[0].length,
        frozenRowCount: 1,
      },
    },
  },
});

const userValue = (value: number | string | null) => {
  const userValue = value === null ? '' : value;
  return typeof userValue === 'number' ? { numberValue: value as number } : { stringValue: value as string };
};

const updateCellsRequest = (sheet: SheetBasicData, sheetId: number) => ({
  updateCells: {
    range: {
      sheetId,
      startRowIndex: 0,
      endRowIndex: sheet.cellData.length,
      startColumnIndex: 0,
      endColumnIndex: sheet.cellData[0].length,
    },
    rows: sheet.cellData.map(row => ({
      values: row.map(value => ({
        userEnteredValue: userValue(value),
        userEnteredFormat: {
          verticalAlignment: 'TOP',
          padding: {
            left: 10,
            right: 5,
          },
          wrapStrategy: 'CLIP',
        },
      })),
    })),
    fields: 'userEnteredValue, userEnteredFormat(verticalAlignment, padding, wrapStrategy)',
  },
});

const autoResizeSheetRequest = (sheet: SheetBasicData, sheetId: number) => ({
  autoResizeDimensions: {
    dimensions: {
      sheetId,
      dimension: 'COLUMNS',
      startIndex: 0,
      endIndex: sheet.cellData[0].length,
    },
  },
});

const columnWidthRequest = (width: ColumnWidth, sheetId: number) => ({
  updateDimensionProperties: {
    range: {
      sheetId,
      dimension: 'COLUMNS',
      startIndex: width.columnIndex,
      endIndex: width.columnIndex + 1,
    },
    properties: {
      pixelSize: width.width,
    },
    fields: 'pixelSize',
  },
});

const addColumnWidths = (sheet: SheetBasicData, sheetId: number) => {
  return sheet.widths?.map(width => columnWidthRequest(width, sheetId)) || [];
};

const rowHeightRequest = (sheetId: number) => ({
  updateDimensionProperties: {
    range: {
      sheetId,
      dimension: 'ROWS',
      startIndex: 0,
    },
    properties: {
      pixelSize: 20,
    },
    fields: 'pixelSize',
  },
});

const columnFormatRequest = (format: ColumnFormat, sheetId: number) => ({
  repeatCell: {
    range: {
      sheetId,
      startRowIndex: 1,
      startColumnIndex: format.columnIndex,
      endColumnIndex: format.columnIndex + 1,
    },
    cell: {
      userEnteredFormat: {
        numberFormat: {
          type: format.type,
          pattern: format.format,
        },
      },
    },
    fields: 'userEnteredFormat(numberFormat)',
  },
});

const addFormats = (sheet: SheetBasicData, sheetId: number) => {
  return sheet.formats?.map(format => columnFormatRequest(format, sheetId)) || [];
};

const columnValidationRequest = (validation: ColumnValidation, sheetId: number) => ({
  setDataValidation: {
    range: {
      sheetId,
      startRowIndex: 1,
      startColumnIndex: validation.columnIndex,
      endColumnIndex: validation.columnIndex + 1,
    },
    rule: validation.rule,
  },
});

const addValidations = (sheet: SheetBasicData, sheetId: number) => {
  return sheet.validations?.map(validation => columnValidationRequest(validation, sheetId)) || [];
};

const addSheet = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] => [
  addSheetRequest(sheet, sheetId),
  ...addFormats(sheet, sheetId),
  ...addValidations(sheet, sheetId),
  rowHeightRequest(sheetId),
  updateCellsRequest(sheet, sheetId),
  autoResizeSheetRequest(sheet, sheetId),
  ...addColumnWidths(sheet, sheetId),
];

export const uploadToGoogleSheets = async (spreadsheetName: string, ...sheetDatas: SheetBasicData[]) => {
  try {
    const gapi = await requestGoogleAccess();
    const createOptions = {
      properties: {
        title: spreadsheetName,
      },
    };
    const spreadsheet = await gapi.client.sheets.spreadsheets
      .create({}, createOptions)
      .then(response => response.result);
    const spreadsheetId = spreadsheet.spreadsheetId ?? '';

    // sheetId can be specified; note: 0 is reserved for the sheet google creates by default
    const requests = sheetDatas
      .reverse() // preserve left to right order for sheet insertion
      .map((sheetData, index) => addSheet(sheetData, index + 1))
      .flat();

    const spreadsheetUpdated = await gapi.client.sheets.spreadsheets
      .batchUpdate({
        spreadsheetId,
        resource: {
          requests,
          includeSpreadsheetInResponse: true,
        },
      })
      .then(response => response.result.updatedSpreadsheet);

    return spreadsheetUpdated;
  } catch (error) {
    console.error(error);
    return null;
  }
};
